import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Row, Col, Form, Button, Spinner, Alert, Modal, InputGroup } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import OrderFinancialSummary, { formatMoney } from "./OrderFinancialSummary";
import "./WebOrderEditPage.css";

const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
    "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia",
    "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
    "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
    "Haiti", "Honduras", "Hungary",
    "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
    "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
    "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
    "Oman",
    "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar",
    "Romania", "Russia", "Rwanda",
    "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
    "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
    "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
    "Yemen",
    "Zambia", "Zimbabwe"
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

const DELIVERY_STEPS = [
    { key: "Pending", label: "Pending", icon: "📦" },
    { key: "Shipped", label: "Shipped", icon: "🚚" },
    { key: "Delivered", label: "Delivered", icon: "✅" }
];

function DeliveryStatusStepper({ status }) {
    const isReturned = status === "Returned";
    const activeIndex = isReturned ? -1 : DELIVERY_STEPS.findIndex((s) => s.key === status);

    return (
        <div className="wo-steps" aria-label={`Delivery status: ${status}`}>
            {DELIVERY_STEPS.map((step, index) => {
                const reached = !isReturned && index <= activeIndex;
                const lineFilled = !isReturned && index < activeIndex;

                return (
                    <React.Fragment key={step.key}>
                        <div className={`wo-step ${reached ? "wo-step--done" : ""}`}>
                            <span className="wo-step__dot">{reached ? "✓" : index + 1}</span>
                            {step.label}
                        </div>
                        {index < DELIVERY_STEPS.length - 1 && (
                            <div className={`wo-steps__line ${lineFilled ? "wo-steps__line--done" : ""}`} />
                        )}
                    </React.Fragment>
                );
            })}
            {isReturned && (
                <>
                    <div className="wo-steps__line" />
                    <div className="wo-step wo-step--returned">
                        <span className="wo-step__dot">↩</span>
                        Returned
                    </div>
                </>
            )}
        </div>
    );
}

function ProductThumb({ src, alt, size = 44, onClick }) {
    const [failed, setFailed] = useState(false);

    const fallback =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>
                <rect width='100%' height='100%' fill='#e9ecef'/>
                <text x='50%' y='50%' font-size='8' fill='#6c757d' text-anchor='middle' dominant-baseline='middle'>No Img</text>
             </svg>`
        );

    return (
        <img
            src={failed || !src ? fallback : src}
            alt={alt}
            style={{ width: `${size}px`, height: `${size}px`, objectFit: "contain", flexShrink: 0, cursor: src && !failed ? "pointer" : "default" }}
            onError={() => setFailed(true)}
            onClick={() => src && !failed && onClick && onClick(src)}
        />
    );
}

export default function WebOrderEditPage() {
    const { id } = useParams();
    const { setSidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const [order, setOrder] = useState(null);
    const [couriers, setCouriers] = useState([]);
    const [products, setProducts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [refreshing, setRefreshing] = useState(false);
    // Summary worked out from values typed but not saved yet.
    const [liveSummary, setLiveSummary] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const savedFinanceKey = useRef(null);
    const previewSeq = useRef(0);

    const [formData, setFormData] = useState({
        name: "", email: "", phone: "", vat_id: "",
        country: "", address: "",
        payment_date: "", payment_message: "",
        courier_company_id: "", tracking_number: "", delivery_status: "Pending", delivery_days: "",
        exchange_rate: "",
        image_1: null,
        image_2: null
    });

    const [imagePreviews, setImagePreviews] = useState({
        image_1: null,
        image_2: null
    });

    const [previewModal, setPreviewModal] = useState({
        show: false,
        imgUrl: "",
        title: ""
    });

    // Put an order from the API into the page state (used on load and after save).
    const applyOrder = useCallback((o) => {
        setOrder(o);
        setSummary(o.financial_summary || null);
        // The values just loaded are the saved ones; the live preview
        // effect records them on its next run.
        setLiveSummary(null);
        savedFinanceKey.current = null;
        previewSeq.current += 1;

        setFormData({
            name: o.buyer?.name || "",
            email: o.buyer?.email || "",
            phone: o.buyer?.phone || "",
            vat_id: o.buyer?.vat_id || "",
            country: o.address?.country || o.buyer?.country || "",
            address: o.address?.address || o.buyer?.address || "",
            payment_date: o.payment?.date ? o.payment.date.split("T")[0] : "",
            payment_message: o.payment?.message || "",
            courier_company_id: o.delivery?.courier_company?.id || o.delivery?.courier_company_id || "",
            tracking_number: o.delivery?.tracking_number || "",
            delivery_status: o.delivery?.status || "Pending",
            delivery_days: o.delivery?.delivery_days ?? "",
            exchange_rate: o.exchange_rate ?? "",
            shipping_charges: o.shipping_charges !== null && o.shipping_charges !== undefined && Number(o.shipping_charges) !== 0
                ? o.shipping_charges
                : "",
            image_1: null,
            image_2: null
        });

        setImagePreviews({
            image_1: o.image_1 ? `${process.env.REACT_APP_IMG_URL}${o.image_1}` : null,
            image_2: o.image_2 ? `${process.env.REACT_APP_IMG_URL}${o.image_2}` : null
        });

        setProducts(
            (o.products || []).map((p) => ({
                id: p.id,
                product_id: p.product_id,
                title: p.title || p.product?.title || `Product #${p.product_id}`,
                product_code: p.product_code || "",
                card_img: p.card_img || p.product?.card_img || null,
                quantity: Number(p.quantity) || 1,
                unit_price: p.unit_price ?? "",
                sell_price_pkr: p.sell_price_pkr ?? "",
                buying_price: p.buying_price ?? "",
                // Saved on the product; shown for reference only.
                product_buying_price: p.product_buying_price ?? null,
                buyer_shipping_usd: p.buyer_shipping_usd ?? null,
                net_weight: p.net_weight ?? "",
                declared_value: p.declared_value ?? "",
                description: p.description || "",
                link: p.link || null
            }))
        );
    }, []);

    const fetchOrder = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [orderRes, courierRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/admin/web-orders/${id}`, { headers: authHeaders() }),
                axios.get(`${process.env.REACT_APP_API_URL}/admin/web-orders/initial-data`, { headers: authHeaders() })
            ]);

            if (orderRes.data?.success) {
                applyOrder(orderRes.data.data);
            } else {
                setError("Order details not found.");
            }

            const courierList = courierRes.data?.courier_companies || courierRes.data || [];
            setCouriers(courierList);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch order details.");
        } finally {
            setLoading(false);
        }
    }, [id, applyOrder]);

    useEffect(() => {
        fetchOrder();
        setSidebar(false);
    }, [fetchOrder]);

    // Everything that changes the profit summary, as one comparable string.
    const financeKey = JSON.stringify({
        rate: formData.exchange_rate === "" ? "" : Number(formData.exchange_rate),
        shipping: formData.shipping_charges === "" || formData.shipping_charges === undefined ? "" : Number(formData.shipping_charges),
        buying: products.map((p) => [p.id, p.buying_price === "" || p.buying_price === null ? "" : Number(p.buying_price)])
    });

    // Recalculate the summary a moment after the admin stops typing.
    // Nothing is saved; the backend uses the same rules as Save.
    useEffect(() => {
        if (!order) return undefined;

        if (savedFinanceKey.current === null) {
            savedFinanceKey.current = financeKey;
            return undefined;
        }

        if (financeKey === savedFinanceKey.current) {
            previewSeq.current += 1;
            setLiveSummary(null);
            setPreviewing(false);
            return undefined;
        }

        const seq = ++previewSeq.current;
        setPreviewing(true);

        const timer = setTimeout(async () => {
            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/admin/web-orders/${id}/preview-financials`,
                    {
                        exchange_rate: formData.exchange_rate === "" ? null : formData.exchange_rate,
                        shipping_charges: formData.shipping_charges === "" ? null : formData.shipping_charges,
                        products: products.map((p) => ({ id: p.id, buying_price: p.buying_price === "" ? null : p.buying_price }))
                    },
                    { headers: authHeaders() }
                );
                if (seq === previewSeq.current && res.data?.data) {
                    setLiveSummary(res.data.data);
                }
            } catch (err) {
                // Invalid numbers while typing (e.g. a negative value): keep the last summary.
            } finally {
                if (seq === previewSeq.current) setPreviewing(false);
            }
        }, 450);

        return () => clearTimeout(timer);
        // financeKey covers the form values used inside.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financeKey, order, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleImageChange = (field, file) => {
        if (file) {
            setFormData((prev) => ({ ...prev, [field]: file }));
            setImagePreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
        }
    };

    const handleProductChange = (index, field, value) => {
        setProducts((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
        setFieldErrors((prev) => ({ ...prev, [`products.${index}.${field}`]: undefined }));
    };

    // Live preview while typing. The saved values always come from the backend.
    const rateNumber = Number(formData.exchange_rate) || 0;
    const previewSellPkr = (p) =>
        rateNumber > 0 && p.unit_price !== "" ? Number(p.unit_price) * (Number(p.quantity) || 1) * rateNumber : null;
    const toUsd = (pkr) => (rateNumber > 0 && pkr !== "" && pkr !== null && pkr !== undefined ? Number(pkr) / rateNumber : null);

    const productLink = (p) => {
        if (p.link?.url) return p.link.url;
        if (p.link?.type === "website" && process.env.REACT_APP_ECOM_URL && p.product_id) {
            return `${process.env.REACT_APP_ECOM_URL}${p.product_id}`;
        }
        if (!p.link && order?.source?.type === "website" && process.env.REACT_APP_ECOM_URL && p.product_id) {
            return `${process.env.REACT_APP_ECOM_URL}${p.product_id}`;
        }
        return null;
    };

    const fieldError = (key) => {
        const message = fieldErrors[key];
        return Array.isArray(message) ? message[0] : message;
    };

    const openImageZoom = (url, title = "Image View") => {
        if (url) {
            setPreviewModal({ show: true, imgUrl: url, title });
        }
    };

    const getCourierName = (c) => {
        return c.company_name || c.name || c.title || `Courier #${c.id}`;
    };

    const buyerShippingUsd = products.reduce((sum, p) => sum + (Number(p.buyer_shipping_usd) || 0), 0);
    const productsUsd = products.reduce((sum, p) => sum + (Number(p.unit_price) || 0) * (Number(p.quantity) || 1), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccessMsg(null);
        setFieldErrors({});

        try {
            const sendFormData = new FormData();

            Object.keys(formData).forEach((key) => {
                if (key !== "image_1" && key !== "image_2") {
                    if (formData[key] !== "" && formData[key] !== null) {
                        sendFormData.append(key, formData[key]);
                    }
                }
            });

            // Empty shipping means 0 for the whole order.
            if (formData.shipping_charges === "") {
                sendFormData.append("shipping_charges", "");
            }

            if (formData.image_1 instanceof File) {
                sendFormData.append("image_1", formData.image_1);
            }
            if (formData.image_2 instanceof File) {
                sendFormData.append("image_2", formData.image_2);
            }

            sendFormData.append("_method", "PUT");

            // Unit price and selling price PKR are not sent: the first comes
            // from the order, the second is calculated by the backend.
            products.forEach((p, index) => {
                sendFormData.append(`products[${index}][id]`, p.id);
                sendFormData.append(`products[${index}][buying_price]`, p.buying_price ?? "");
                sendFormData.append(`products[${index}][net_weight]`, p.net_weight ?? "");
                sendFormData.append(`products[${index}][declared_value]`, p.declared_value ?? "");
                if (p.description !== "") sendFormData.append(`products[${index}][description]`, p.description);
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/web-orders/${id}`,
                sendFormData,
                {
                    headers: {
                        ...authHeaders(),
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (response.data?.success) {
                applyOrder(response.data.data);
                setSuccessMsg("Order saved. Totals and commissions have been recalculated.");
            } else {
                setError(response.data?.message || "Failed to update the order.");
            }
        } catch (err) {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFieldErrors(err.response.data.errors);
                setError("Please fix the highlighted fields.");
            } else {
                setError(err.response?.data?.message || err.response?.data?.error || "Failed to update the order.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleRefreshRates = async () => {
        setRefreshing(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/web-orders/${id}/recalculate`,
                { refresh_rates: true },
                { headers: authHeaders() }
            );

            if (response.data?.success) {
                applyOrder(response.data.data);
                setSuccessMsg("Recalculated with the current commission rates.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to recalculate the order.");
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <PageLayout hideSidebar={true}>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading order information...</p>
                </div>
            </PageLayout>
        );
    }

    const handlePrint = () => {
        const printUrl = process.env.REACT_APP_URL || "http://localhost:8000/order-print";
        window.open(`${printUrl}/orders/${id}/pdf`, "_blank");
    };

    const isWebsite = order?.source?.type === "website";
    // Store seller info per product, from the backend calculation.
    const summaryItems = Object.fromEntries((summary?.items || []).map((item) => [item.id, item]));
    const orderedAt = order?.dates?.ordered_at ? new Date(order.dates.ordered_at) : null;
    const selectedCourier = couriers.find((c) => String(c.id) === String(formData.courier_company_id));
    const shippingPkr = formData.shipping_charges === "" ? 0 : Number(formData.shipping_charges);

    const renderAttachment = (field, label) => (
        <div>
            <Form.Label>{label}</Form.Label>
            <div className="wo-attach__frame">
                {imagePreviews[field] ? (
                    <img
                        src={imagePreviews[field]}
                        alt={label}
                        onClick={() => openImageZoom(imagePreviews[field], label)}
                        title="Click to enlarge"
                    />
                ) : (
                    <span className="small text-muted">No image uploaded</span>
                )}
            </div>
            <Form.Control
                type="file"
                size="sm"
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={(e) => handleImageChange(field, e.target.files[0])}
            />
        </div>
    );

    return (
        <PageLayout hideSidebar={true}>
            <div className="wo-page">
                <div className="mc-card mb-4">
                    <div className="mc-breadcrumb">
                        <h3 className="mc-breadcrumb-title">Order {order?.order_number || `#${id}`}</h3>
                        <ul className="mc-breadcrumb-list">
                            <li className="mc-breadcrumb-item">
                                <Link to="#" className="mc-breadcrumb-link">{t("home") || "Home"}</Link>
                            </li>
                            <li className="mc-breadcrumb-item">
                                <Link to="/admin/web-orders" className="mc-breadcrumb-link">Web Orders</Link>
                            </li>
                            <li className="mc-breadcrumb-item">Order details</li>
                        </ul>
                    </div>
                </div>

                {/* Order header */}
                <div className="wo-header">
                    <div className="wo-header__top">
                        <div>
                            <h2 className="wo-header__number">{order?.order_number || `#${id}`}</h2>
                            <div className="wo-header__meta">
                                <span className="wo-chip wo-chip--source">
                                    {order?.source?.label || (isWebsite ? "Watches Pool Website" : "Marketplace")}
                                </span>
                                {order?.source?.mplacer && (
                                    <span className="wo-chip">MPlacer: {order.source.mplacer}</span>
                                )}
                                {!isWebsite && order?.payment?.status && (
                                    <span className="wo-chip">Payment: {order.payment.status}</span>
                                )}
                                {(order?.product_codes || []).map((code) => (
                                    <span key={code} className="wo-chip wo-chip--code">{code}</span>
                                ))}
                                {orderedAt && (
                                    <span className="wo-chip wo-chip--quiet">
                                        Ordered {orderedAt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="wo-header__actions">
                            <Button className="wo-btn-quiet" onClick={handlePrint}>Print invoice</Button>
                            <Button className="wo-btn-quiet" onClick={() => navigate("/admin/web-orders")}>Back to orders</Button>
                        </div>
                    </div>
                    <DeliveryStatusStepper status={formData.delivery_status} />
                </div>

                {error && !submitting && Object.keys(fieldErrors).length === 0 && !order && (
                    <Alert variant="danger">{error}</Alert>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                    <Row className="g-4">
                        {/* Left: products */}
                        <Col lg={7}>
                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <div>
                                        <h3 className="wo-panel__title">Products</h3>
                                        <p className="wo-panel__hint">
                                            {products.length} {products.length === 1 ? "item" : "items"}. Enter the buying price, declared value and weight for each.
                                        </p>
                                    </div>
                                    <Form.Group className="wo-rate mb-0" controlId="wo-exchange-rate">
                                        <Form.Label>Exchange rate for this order</Form.Label>
                                        <InputGroup hasValidation>
                                            <InputGroup.Text>Rs</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="exchange_rate"
                                                value={formData.exchange_rate}
                                                onChange={handleChange}
                                                placeholder="0.00"
                                                isInvalid={!!fieldError("exchange_rate")}
                                            />
                                            <InputGroup.Text>per $1</InputGroup.Text>
                                            <Form.Control.Feedback type="invalid">{fieldError("exchange_rate")}</Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                </header>

                                {products.length === 0 ? (
                                    <div className="wo-panel__body text-muted">This order has no products.</div>
                                ) : (
                                    <ul className="wo-products">
                                        {products.map((p, index) => {
                                            const link = productLink(p);
                                            const sellPkr = previewSellPkr(p);
                                            const storeSeller = summaryItems[p.id]?.store_seller || null;
                                            const buyingMissing = !storeSeller && p.buying_price === "";

                                            return (
                                                <li key={p.id} className="wo-product">
                                                    <div className="wo-product__thumb">
                                                        <ProductThumb
                                                            src={p.card_img ? `${process.env.REACT_APP_IMG_URL}${p.card_img}` : null}
                                                            alt={p.title}
                                                            size={68}
                                                            onClick={(src) => openImageZoom(src, p.title)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="wo-product__top">
                                                            {p.product_code && <span className="wo-chip wo-chip--code">{p.product_code}</span>}
                                                            {p.link?.marketplace && <span className="small text-muted">on {p.link.marketplace}</span>}
                                                            {storeSeller && (
                                                                <span className="wo-chip wo-chip--seller">
                                                                    Store seller: {storeSeller.name}
                                                                    {" "}({storeSeller.storage_preference === "admin_store" ? "kept in company store" : "kept in own store"})
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="wo-product__title">
                                                            {link ? (
                                                                <a href={link} target="_blank" rel="noopener noreferrer">
                                                                    {p.title} ↗
                                                                </a>
                                                            ) : (
                                                                p.title
                                                            )}
                                                        </div>
                                                        {!link && p.link?.missing_reason && (
                                                            <div className="wo-product__nolink">No listing link: {p.link.missing_reason}</div>
                                                        )}

                                                        <dl className="wo-product__facts">
                                                            <div>
                                                                <dt>Unit price</dt>
                                                                <dd className="wo-num">{formatMoney(p.unit_price, "USD")}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Selling price</dt>
                                                                <dd className="wo-num">{sellPkr !== null ? formatMoney(sellPkr, "PKR") : "Add exchange rate"}</dd>
                                                            </div>
                                                            <div title="Saved on the product. Not used in this order's profit.">
                                                                <dt>Product buying price</dt>
                                                                <dd className="wo-num wo-ref">
                                                                    {p.product_buying_price != null && p.product_buying_price !== ""
                                                                        ? formatMoney(p.product_buying_price, "PKR")
                                                                        : "—"}
                                                                </dd>
                                                            </div>
                                                            {p.quantity > 1 && (
                                                                <div>
                                                                    <dt>Quantity</dt>
                                                                    <dd className="wo-num">{p.quantity}</dd>
                                                                </div>
                                                            )}
                                                        </dl>

                                                        <div className="wo-product__inputs">
                                                            <Form.Group controlId={`wo-buying-${p.id}`}>
                                                                <Form.Label>Buying price (PKR)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={p.buying_price}
                                                                    onChange={(e) => handleProductChange(index, "buying_price", e.target.value)}
                                                                    placeholder={storeSeller ? "Optional" : "Required"}
                                                                    isInvalid={!!fieldError(`products.${index}.buying_price`)}
                                                                />
                                                                <Form.Control.Feedback type="invalid">{fieldError(`products.${index}.buying_price`)}</Form.Control.Feedback>
                                                                {buyingMissing && !fieldError(`products.${index}.buying_price`) && (
                                                                    <div className="wo-missing">Add to calculate profit</div>
                                                                )}
                                                            </Form.Group>
                                                            <Form.Group controlId={`wo-declared-${p.id}`}>
                                                                <Form.Label>Declared value</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={p.declared_value}
                                                                    onChange={(e) => handleProductChange(index, "declared_value", e.target.value)}
                                                                    isInvalid={!!fieldError(`products.${index}.declared_value`)}
                                                                />
                                                                <Form.Control.Feedback type="invalid">{fieldError(`products.${index}.declared_value`)}</Form.Control.Feedback>
                                                            </Form.Group>
                                                            <Form.Group controlId={`wo-weight-${p.id}`}>
                                                                <Form.Label>Net weight (kg)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={p.net_weight}
                                                                    onChange={(e) => handleProductChange(index, "net_weight", e.target.value)}
                                                                    isInvalid={!!fieldError(`products.${index}.net_weight`)}
                                                                />
                                                                <Form.Control.Feedback type="invalid">{fieldError(`products.${index}.net_weight`)}</Form.Control.Feedback>
                                                            </Form.Group>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                <div className="wo-totals">
                                    <div><span>Products</span><strong>{formatMoney(productsUsd, "USD")}</strong></div>
                                    {buyerShippingUsd > 0 && (
                                        <div><span>Shipping paid by buyer</span><strong>{formatMoney(buyerShippingUsd, "USD")}</strong></div>
                                    )}
                                    <div><span>Order total</span><strong>{formatMoney(productsUsd + buyerShippingUsd, "USD")}</strong></div>
                                </div>
                            </section>

                            {/* Shipment */}
                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <div>
                                        <h3 className="wo-panel__title">Shipment</h3>
                                        <p className="wo-panel__hint">Enter the shipping cost once for the whole order. It is split across the products.</p>
                                    </div>
                                </header>
                                <div className="wo-panel__body">
                                    <Form.Group controlId="wo-shipping" className="mb-3">
                                        <Form.Label>Shipping charges</Form.Label>
                                        <InputGroup hasValidation>
                                            <InputGroup.Text>Rs</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="shipping_charges"
                                                value={formData.shipping_charges}
                                                onChange={handleChange}
                                                placeholder="0.00"
                                                isInvalid={!!fieldError("shipping_charges")}
                                            />
                                            <Form.Control.Feedback type="invalid">{fieldError("shipping_charges")}</Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                    <div className="wo-ship__line">
                                        <span>In USD</span>
                                        <strong className="wo-num">{rateNumber > 0 ? formatMoney(toUsd(shippingPkr), "USD") : "Add exchange rate"}</strong>
                                    </div>
                                    <div className="wo-ship__line">
                                        <span>Courier</span>
                                        <strong>{selectedCourier ? getCourierName(selectedCourier) : "Not selected"}</strong>
                                    </div>
                                    <div className="wo-ship__line">
                                        <span>Tracking number</span>
                                        <strong>{formData.tracking_number || "Not added"}</strong>
                                    </div>
                                </div>
                            </section>

                            {/* Profit summary */}
                            <OrderFinancialSummary
                                summary={liveSummary || summary}
                                unsaved={!!liveSummary}
                                updating={previewing}
                                onRefreshRates={summary && !liveSummary ? handleRefreshRates : null}
                                refreshing={refreshing}
                            />
                        </Col>

                        {/* Right: buyer, delivery, payment, attachments */}
                        <Col lg={5}>
                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <h3 className="wo-panel__title">Buyer &amp; shipping address</h3>
                                </header>
                                <div className="wo-panel__body">
                                    <Row className="g-3">
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-name">
                                                <Form.Label>Name</Form.Label>
                                                <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} isInvalid={!!fieldError("name")} />
                                                <Form.Control.Feedback type="invalid">{fieldError("name")}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-phone">
                                                <Form.Label>Phone</Form.Label>
                                                <Form.Control type="text" name="phone" value={formData.phone} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-email">
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} isInvalid={!!fieldError("email")} />
                                                <Form.Control.Feedback type="invalid">{fieldError("email")}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-vat">
                                                <Form.Label>VAT / Tax ID</Form.Label>
                                                <Form.Control type="text" name="vat_id" value={formData.vat_id} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={12}>
                                            <Form.Group controlId="wo-country">
                                                <Form.Label>Country</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="country"
                                                    list="country-options"
                                                    value={formData.country}
                                                    onChange={handleChange}
                                                    placeholder="Search or select country"
                                                    isInvalid={!!fieldError("country")}
                                                />
                                                <datalist id="country-options">
                                                    {countries.map((c) => (
                                                        <option key={c} value={c} />
                                                    ))}
                                                </datalist>
                                                <Form.Control.Feedback type="invalid">{fieldError("country")}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={12}>
                                            <Form.Group controlId="wo-address">
                                                <Form.Label>Full address</Form.Label>
                                                <Form.Control as="textarea" rows={3} name="address" value={formData.address} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </section>

                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <h3 className="wo-panel__title">Delivery</h3>
                                </header>
                                <div className="wo-panel__body">
                                    <Row className="g-3">
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-delivery-status">
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select name="delivery_status" value={formData.delivery_status} onChange={handleChange}>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Shipped">Shipped</option>
                                                    <option value="Delivered">Delivered</option>
                                                    <option value="Returned">Returned</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-courier">
                                                <Form.Label>Courier</Form.Label>
                                                <Form.Select name="courier_company_id" value={formData.courier_company_id} onChange={handleChange}>
                                                    <option value="">Select courier</option>
                                                    {couriers.map((c) => (
                                                        <option key={c.id} value={c.id}>{getCourierName(c)}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-tracking">
                                                <Form.Label>Tracking number</Form.Label>
                                                <Form.Control type="text" name="tracking_number" value={formData.tracking_number} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="wo-days">
                                                <Form.Label>Delivery days</Form.Label>
                                                <Form.Control type="number" min="0" name="delivery_days" value={formData.delivery_days} onChange={handleChange} isInvalid={!!fieldError("delivery_days")} />
                                                <Form.Control.Feedback type="invalid">{fieldError("delivery_days")}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </section>

                            {!isWebsite && (
                                <section className="wo-panel">
                                    <header className="wo-panel__head">
                                        <h3 className="wo-panel__title">Payment</h3>
                                    </header>
                                    <div className="wo-panel__body">
                                        <Row className="g-3">
                                            <Col sm={12}>
                                                <Form.Group controlId="wo-payment-date">
                                                    <Form.Label>Payment date</Form.Label>
                                                    <Form.Control type="date" name="payment_date" value={formData.payment_date} onChange={handleChange} />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={12}>
                                                <Form.Group controlId="wo-payment-message">
                                                    <Form.Label>Payment note</Form.Label>
                                                    <Form.Control as="textarea" rows={2} name="payment_message" value={formData.payment_message} onChange={handleChange} placeholder="Transaction reference or notes" />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </div>
                                </section>
                            )}

                            {!isWebsite && (
                                <section className="wo-panel">
                                    <header className="wo-panel__head">
                                        <div>
                                            <h3 className="wo-panel__title">Attachments</h3>
                                            <p className="wo-panel__hint">Uploaded by the MPlacer when the order was punched.</p>
                                        </div>
                                    </header>
                                    <div className="wo-panel__body">
                                        <div className="wo-attach">
                                            {renderAttachment("image_1", "Address picture")}
                                            {renderAttachment("image_2", "Earning picture")}
                                        </div>
                                    </div>
                                </section>
                            )}
                        </Col>

                    </Row>

                    <div className="wo-savebar">
                        <span
                            className={`wo-savebar__msg ${error ? "wo-savebar__msg--error" : successMsg ? "wo-savebar__msg--ok" : ""}`}
                            role="status"
                            aria-live="polite"
                        >
                            {error || successMsg || ""}
                        </span>
                        <Button className="wo-btn-quiet" onClick={() => navigate("/admin/web-orders")}>Cancel</Button>
                        <Button type="submit" className="wo-btn-primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Saving
                                </>
                            ) : (
                                "Save and recalculate"
                            )}
                        </Button>
                    </div>
                </Form>
            </div>

            {/* Lightbox / Zoom Preview Modal */}
            <Modal
                show={previewModal.show}
                onHide={() => setPreviewModal({ show: false, imgUrl: "", title: "" })}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">{previewModal.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3 bg-dark rounded-bottom">
                    <img
                        src={previewModal.imgUrl}
                        alt="Enlarged Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: "80vh", objectFit: "contain" }}
                    />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}